import type { DashboardNarrativeContent } from "src/components/common";
import type { DashboardNarrativeSegment } from "src/services/DashboardNarrativeAPI";
import {
  computeChangePercent,
  type OmnichannelCustomer,
  type OmnichannelOrder,
  type OmnichannelProduct,
  type OmnichannelTabId,
} from "./OmnichannelAnalyticsTabsDashboard.data";

export type OmnichannelSummaryTab = OmnichannelTabId;

interface OmnichannelKpis {
  totalRevenue: number;
  totalOrders: number;
  activeCustomers: number;
  avgOrderValue: number;
  inventoryValue: number;
  avgCltv: number;
}

interface OmnichannelDistributionPoint {
  label: string;
  value: number;
  percentage?: number;
}

interface OmnichannelFinancialSummary {
  revenue: number;
  cogs: number;
  grossProfit: number;
  taxes: number;
  expenses: number;
  netProfit: number;
  refunds: number;
}

interface OmnichannelMonthlyPoint {
  month: string;
  revenue: number;
  expenses: number;
  profit: number;
}

interface OmnichannelDateRange {
  startDate: string;
  endDate: string;
}

interface SummaryFormatters {
  formatCurrency: (value: number) => string;
  formatCompactCurrency: (value: number) => string;
  formatNumber: (value: number) => string;
  isRTL: boolean;
}

interface BuildSummaryParams {
  tab: OmnichannelSummaryTab;
  fmt: SummaryFormatters;
  dateRange: OmnichannelDateRange;
  kpis?: OmnichannelKpis;
  previousKpis?: OmnichannelKpis;
  channelData?: OmnichannelDistributionPoint[];
  segmentation?: OmnichannelDistributionPoint[];
  products?: OmnichannelProduct[];
  orders?: OmnichannelOrder[];
  customers?: OmnichannelCustomer[];
  summary?: OmnichannelFinancialSummary;
  previousSummary?: OmnichannelFinancialSummary;
  monthly?: OmnichannelMonthlyPoint[];
  taxBreakdown?: OmnichannelDistributionPoint[];
}

function movement(delta: number | null, isRTL: boolean): string {
  if (delta === null) {
    return isRTL ? "تغيّرت" : "changed";
  }
  const abs = Math.abs(delta);
  const direction = delta >= 0
    ? (isRTL ? "ارتفع" : "rose")
    : (isRTL ? "انخفض" : "fell");
  const stable = isRTL ? "ظل مستقراً نسبياً" : "stayed broadly flat";

  if (abs < 2) {return stable;}
  if (abs < 5) {return isRTL ? `${direction} قليلاً` : `${direction} slightly`;}
  if (abs <= 15) {return direction;}
  return isRTL ? `${direction} بشكل ملحوظ` : `${direction} sharply`;
}

function pct(delta: number | null, isRTL: boolean): string {
  if (delta === null) {
    return isRTL ? "غير متاح" : "n/a";
  }
  const abs = Math.abs(delta);
  return isRTL ? `${abs}%` : `${abs}%`;
}

function buildOverviewInsights(params: BuildSummaryParams): string[] {
  const { fmt } = params;
  const { isRTL } = fmt;
  const insights: string[] = [];
  const kpis = params.kpis;
  const previousKpis = params.previousKpis;
  const channelData = params.channelData ?? [];
  const segmentation = params.segmentation ?? [];
  const products = params.products ?? [];

  if (kpis && previousKpis) {
    const revenueDelta = computeChangePercent(kpis.totalRevenue, previousKpis.totalRevenue);
    insights.push(
      isRTL
        ? `بلغت الإيرادات ${fmt.formatCompactCurrency(kpis.totalRevenue)} حيث ${movement(revenueDelta, isRTL)} بنسبة ${pct(revenueDelta, isRTL)} مقارنة بالفترة السابقة.`
        : `Revenue reached ${fmt.formatCompactCurrency(kpis.totalRevenue)}, having ${movement(revenueDelta, isRTL)} ${pct(revenueDelta, isRTL)} versus the prior period.`,
    );

    const aovDelta = computeChangePercent(kpis.avgOrderValue, previousKpis.avgOrderValue);
    insights.push(
      isRTL
        ? `متوسط قيمة الطلب ${movement(aovDelta, isRTL)} إلى ${fmt.formatCurrency(kpis.avgOrderValue)} عبر ${fmt.formatNumber(kpis.totalOrders)} طلب.`
        : `Average order value ${movement(aovDelta, isRTL)} to ${fmt.formatCurrency(kpis.avgOrderValue)} across ${fmt.formatNumber(kpis.totalOrders)} orders.`,
    );

    const customersDelta = computeChangePercent(kpis.activeCustomers, previousKpis.activeCustomers);
    insights.push(
      isRTL
        ? `عدد العملاء النشطين ${movement(customersDelta, isRTL)} إلى ${fmt.formatNumber(kpis.activeCustomers)} عميل، بمتوسط قيمة دائمة ${fmt.formatCurrency(kpis.avgCltv)}.`
        : `Active customers ${movement(customersDelta, isRTL)} to ${fmt.formatNumber(kpis.activeCustomers)}, with an average CLTV of ${fmt.formatCurrency(kpis.avgCltv)}.`,
    );
  }

  const topChannel = [...channelData].sort((a, b) => b.value - a.value)[0];
  if (topChannel) {
    insights.push(
      isRTL
        ? `تتصدر قناة ${topChannel.label === "Online Store" ? "المتجر الإلكتروني" : "المتجر الفعلي"} توزيع القنوات بحصة ${topChannel.percentage ?? 0}% من الإيرادات.`
        : `${topChannel.label} leads channel distribution with a ${topChannel.percentage ?? 0}% share of revenue.`,
    );
  }

  const newSeg = segmentation.find(item => item.label === "New");
  const returningSeg = segmentation.find(item => item.label === "Returning");
  if (newSeg && returningSeg) {
    const totalCustomers = newSeg.value + returningSeg.value;
    const returningShare = totalCustomers > 0 ? Math.round((returningSeg.value / totalCustomers) * 100) : 0;
    insights.push(
      isRTL
        ? `يمثل العملاء المتكررون ${returningShare}% من قاعدة العملاء، مقابل ${newSeg.value} عميل جديد خلال الفترة.`
        : `Returning customers make up ${returningShare}% of the customer base, alongside ${newSeg.value} new customers this period.`,
    );
  }

  const lowStock = products.filter(product => product.status === "low_stock").length;
  if (products.length > 0) {
    insights.push(
      isRTL
        ? `يوجد ${fmt.formatNumber(lowStock)} منتج بمخزون منخفض من إجمالي ${fmt.formatNumber(products.length)} منتج في الكتالوج الموحّد.`
        : `${fmt.formatNumber(lowStock)} of ${fmt.formatNumber(products.length)} catalog products are running low on stock.`,
    );
  }

  return insights.slice(0, 5);
}

function buildSalesInsights(params: BuildSummaryParams): string[] {
  const { fmt } = params;
  const { isRTL } = fmt;
  const insights: string[] = [];
  const orders = params.orders ?? [];
  const channelData = params.channelData ?? [];

  const completed = orders.filter(order => order.status === "completed");
  const pending = orders.filter(order => order.status === "pending");
  const refunded = orders.filter(order => order.status === "refunded");
  const cancelled = orders.filter(order => order.status === "cancelled");
  const totalRevenue = completed.reduce((sum, order) => sum + order.total, 0);

  insights.push(
    isRTL
      ? `حققت ${fmt.formatNumber(completed.length)} طلب مكتمل إيرادات إجمالية بلغت ${fmt.formatCompactCurrency(totalRevenue)} خلال الفترة المحددة.`
      : `${fmt.formatNumber(completed.length)} completed orders generated ${fmt.formatCompactCurrency(totalRevenue)} in total revenue for the selected period.`,
  );

  const topChannel = [...channelData].sort((a, b) => b.value - a.value)[0];
  const bottomChannel = [...channelData].sort((a, b) => a.value - b.value)[0];
  if (topChannel && bottomChannel && topChannel.label !== bottomChannel.label) {
    insights.push(
      isRTL
        ? `${topChannel.label === "Online Store" ? "المتجر الإلكتروني" : "المتجر الفعلي"} يقود المبيعات بإيرادات ${fmt.formatCompactCurrency(topChannel.value)}، بينما يتخلف ${bottomChannel.label === "Online Store" ? "المتجر الإلكتروني" : "المتجر الفعلي"} عند ${fmt.formatCompactCurrency(bottomChannel.value)}.`
        : `${topChannel.label} leads sales with ${fmt.formatCompactCurrency(topChannel.value)} in revenue, while ${bottomChannel.label} trails at ${fmt.formatCompactCurrency(bottomChannel.value)}.`,
    );
  }

  const cancelRate = orders.length > 0 ? cancelled.length / orders.length : 0;
  insights.push(
    isRTL
      ? `معدل الإلغاء ${Math.round(cancelRate * 100)}%، مع ${fmt.formatNumber(pending.length)} طلب قيد الانتظار حالياً.`
      : `Cancellation rate stands at ${Math.round(cancelRate * 100)}%, with ${fmt.formatNumber(pending.length)} orders currently pending.`,
  );

  const refundValue = refunded.reduce((sum, order) => sum + order.total, 0);
  insights.push(
    isRTL
      ? `المبالغ المستردة بلغت ${fmt.formatCompactCurrency(refundValue)} عبر ${fmt.formatNumber(refunded.length)} طلب.`
      : `Refunds totalled ${fmt.formatCompactCurrency(refundValue)} across ${fmt.formatNumber(refunded.length)} orders.`,
  );

  const zidOrders = orders.filter(order => order.source === "zid").length;
  const odooOrders = orders.filter(order => order.source === "odoo").length;
  insights.push(
    isRTL
      ? `توزّعت الطلبات بين Zid (${fmt.formatNumber(zidOrders)}) وOdoo (${fmt.formatNumber(odooOrders)}).`
      : `Order volume split between Zid (${fmt.formatNumber(zidOrders)}) and Odoo (${fmt.formatNumber(odooOrders)}).`,
  );

  return insights.slice(0, 5);
}

function buildInventoryInsights(params: BuildSummaryParams): string[] {
  const { fmt } = params;
  const { isRTL } = fmt;
  const insights: string[] = [];
  const products = params.products ?? [];

  const inStock = products.filter(product => product.status === "in_stock").length;
  const lowStock = products.filter(product => product.status === "low_stock").length;
  const outOfStock = products.filter(product => product.status === "out_of_stock").length;
  const inventoryValue = products.reduce((sum, product) => sum + product.totalStock * product.cost, 0);

  insights.push(
    isRTL
      ? `يضم الكتالوج الموحّد ${fmt.formatNumber(products.length)} منتج، منها ${fmt.formatNumber(inStock)} متوفر بنسبة ${products.length > 0 ? Math.round((inStock / products.length) * 100) : 0}%.`
      : `The unified catalog holds ${fmt.formatNumber(products.length)} products, of which ${fmt.formatNumber(inStock)} (${products.length > 0 ? Math.round((inStock / products.length) * 100) : 0}%) are in stock.`,
  );

  insights.push(
    isRTL
      ? `قيمة المخزون الحالية تُقدّر بـ ${fmt.formatCompactCurrency(inventoryValue)}.`
      : `Current inventory is valued at ${fmt.formatCompactCurrency(inventoryValue)}.`,
  );

  insights.push(
    isRTL
      ? `${fmt.formatNumber(lowStock)} منتج بمخزون منخفض يحتاج إلى إعادة طلب، و${fmt.formatNumber(outOfStock)} نفد مخزونه بالكامل.`
      : `${fmt.formatNumber(lowStock)} products are low on stock and need reordering, and ${fmt.formatNumber(outOfStock)} are completely out of stock.`,
  );

  const totalZidStock = products.reduce((sum, product) => sum + product.zidStock, 0);
  const totalOdooStock = products.reduce((sum, product) => sum + product.odooStock, 0);
  const strongerSource = totalZidStock >= totalOdooStock ? "Zid" : "Odoo";
  insights.push(
    isRTL
      ? `يحمل مصدر ${strongerSource} المخزون الأكبر (Zid: ${fmt.formatNumber(totalZidStock)}، Odoo: ${fmt.formatNumber(totalOdooStock)}).`
      : `${strongerSource} carries the larger share of stock (Zid: ${fmt.formatNumber(totalZidStock)}, Odoo: ${fmt.formatNumber(totalOdooStock)}).`,
  );

  const mostStocked = [...products].sort((a, b) => b.totalStock - a.totalStock)[0];
  if (mostStocked) {
    insights.push(
      isRTL
        ? `${mostStocked.nameAr} يملك أعلى مستوى مخزون بين المنتجات عند ${fmt.formatNumber(mostStocked.totalStock)} وحدة.`
        : `${mostStocked.name} carries the highest stock level of any product, at ${fmt.formatNumber(mostStocked.totalStock)} units.`,
    );
  }

  return insights.slice(0, 5);
}

function buildCustomersInsights(params: BuildSummaryParams): string[] {
  const { fmt } = params;
  const { isRTL } = fmt;
  const insights: string[] = [];
  const customers = params.customers ?? [];
  const segmentation = params.segmentation ?? [];
  const kpis = params.kpis;
  const previousKpis = params.previousKpis;

  const newSeg = segmentation.find(item => item.label === "New")?.value ?? 0;
  const returningSeg = segmentation.find(item => item.label === "Returning")?.value ?? 0;

  insights.push(
    isRTL
      ? `تضم قاعدة العملاء ${fmt.formatNumber(customers.length)} عميل، منهم ${fmt.formatNumber(newSeg)} عميل جديد و${fmt.formatNumber(returningSeg)} عميل متكرر.`
      : `The customer base includes ${fmt.formatNumber(customers.length)} customers, split between ${fmt.formatNumber(newSeg)} new and ${fmt.formatNumber(returningSeg)} returning.`,
  );

  if (kpis && previousKpis) {
    const cltvDelta = computeChangePercent(kpis.avgCltv, previousKpis.avgCltv);
    insights.push(
      isRTL
        ? `متوسط القيمة الدائمة للعميل ${movement(cltvDelta, isRTL)} إلى ${fmt.formatCurrency(kpis.avgCltv)}.`
        : `Average customer lifetime value ${movement(cltvDelta, isRTL)} to ${fmt.formatCurrency(kpis.avgCltv)}.`,
    );

    const aovDelta = computeChangePercent(kpis.avgOrderValue, previousKpis.avgOrderValue);
    insights.push(
      isRTL
        ? `متوسط حجم السلة ${movement(aovDelta, isRTL)} إلى ${fmt.formatCurrency(kpis.avgOrderValue)}.`
        : `Average basket size ${movement(aovDelta, isRTL)} to ${fmt.formatCurrency(kpis.avgOrderValue)}.`,
    );
  }

  const topSpender = [...customers].sort((a, b) => b.totalSpent - a.totalSpent)[0];
  if (topSpender) {
    insights.push(
      isRTL
        ? `${topSpender.nameAr} هو الأعلى إنفاقاً بإجمالي ${fmt.formatCurrency(topSpender.totalSpent)} عبر ${fmt.formatNumber(topSpender.totalOrders)} طلب.`
        : `${topSpender.name} is the top spender at ${fmt.formatCurrency(topSpender.totalSpent)} across ${fmt.formatNumber(topSpender.totalOrders)} orders.`,
    );
  }

  const zidCustomers = customers.filter(customer => customer.source === "zid").length;
  const odooCustomers = customers.filter(customer => customer.source === "odoo").length;
  insights.push(
    isRTL
      ? `توزّع العملاء بين Zid (${fmt.formatNumber(zidCustomers)}) وOdoo (${fmt.formatNumber(odooCustomers)}).`
      : `Customers are split between Zid (${fmt.formatNumber(zidCustomers)}) and Odoo (${fmt.formatNumber(odooCustomers)}).`,
  );

  return insights.slice(0, 5);
}

function buildFinancialsInsights(params: BuildSummaryParams): string[] {
  const { fmt } = params;
  const { isRTL } = fmt;
  const insights: string[] = [];
  const summary = params.summary;
  const previousSummary = params.previousSummary;
  const monthly = params.monthly ?? [];
  const channelData = params.channelData ?? [];

  if (summary) {
    const margin = summary.revenue > 0 ? Math.round((summary.netProfit / summary.revenue) * 100) : 0;
    insights.push(
      isRTL
        ? `بلغت الإيرادات ${fmt.formatCompactCurrency(summary.revenue)} بصافي ربح ${fmt.formatCompactCurrency(summary.netProfit)} وهامش ${margin}%.`
        : `Revenue reached ${fmt.formatCompactCurrency(summary.revenue)} with a net profit of ${fmt.formatCompactCurrency(summary.netProfit)}, a ${margin}% margin.`,
    );

    if (previousSummary) {
      const profitDelta = computeChangePercent(summary.netProfit, previousSummary.netProfit);
      insights.push(
        isRTL
          ? `صافي الربح ${movement(profitDelta, isRTL)} مقارنة بالفترة السابقة.`
          : `Net profit ${movement(profitDelta, isRTL)} versus the prior period.`,
      );
    }

    insights.push(
      isRTL
        ? `تكلفة البضاعة المباعة بلغت ${fmt.formatCompactCurrency(summary.cogs)} والمصروفات التشغيلية ${fmt.formatCompactCurrency(summary.expenses)}.`
        : `Cost of goods sold came to ${fmt.formatCompactCurrency(summary.cogs)}, with operating expenses of ${fmt.formatCompactCurrency(summary.expenses)}.`,
    );

    insights.push(
      isRTL
        ? `الضرائب المحصّلة ${fmt.formatCompactCurrency(summary.taxes)} والمبالغ المستردة ${fmt.formatCompactCurrency(summary.refunds)}.`
        : `Taxes collected totalled ${fmt.formatCompactCurrency(summary.taxes)}, against ${fmt.formatCompactCurrency(summary.refunds)} in refunds.`,
    );
  }

  const bestMonth = [...monthly].sort((a, b) => b.profit - a.profit)[0];
  if (bestMonth) {
    insights.push(
      isRTL
        ? `أفضل شهر من حيث الربحية كان ${bestMonth.month} بربح ${fmt.formatCompactCurrency(bestMonth.profit)}.`
        : `The strongest month for profitability was ${bestMonth.month}, at ${fmt.formatCompactCurrency(bestMonth.profit)}.`,
    );
  } else {
    const topChannel = [...channelData].sort((a, b) => b.value - a.value)[0];
    if (topChannel) {
      insights.push(
        isRTL
          ? `${topChannel.label === "Online Store" ? "المتجر الإلكتروني" : "المتجر الفعلي"} يقود الإيرادات بحصة القناة الأكبر.`
          : `${topChannel.label} leads revenue with the largest channel share.`,
      );
    }
  }

  return insights.slice(0, 5);
}

function buildLeadSummary(tab: OmnichannelSummaryTab, params: BuildSummaryParams): string {
  const { fmt } = params;
  const { isRTL } = fmt;

  if (tab === "overview") {
    const kpis = params.kpis;
    const previousKpis = params.previousKpis;
    if (kpis && previousKpis) {
      const revenueDelta = computeChangePercent(kpis.totalRevenue, previousKpis.totalRevenue);
      const aovDelta = computeChangePercent(kpis.avgOrderValue, previousKpis.avgOrderValue);
      const channelData = params.channelData ?? [];
      const topChannel = [...channelData].sort((a, b) => b.value - a.value)[0];
      const products = params.products ?? [];
      const lowStock = products.filter(product => product.status === "low_stock").length;

      return isRTL
        ? `بلغت الإيرادات ${fmt.formatCompactCurrency(kpis.totalRevenue)} عبر ${fmt.formatNumber(kpis.totalOrders)} طلب، حيث ${movement(revenueDelta, isRTL)} مقارنة بالفترة السابقة. يبلغ عدد العملاء النشطين ${fmt.formatNumber(kpis.activeCustomers)} بمتوسط قيمة دائمة ${fmt.formatCurrency(kpis.avgCltv)}، ومتوسط قيمة الطلب ${fmt.formatCurrency(kpis.avgOrderValue)} وقد ${movement(aovDelta, isRTL)}.${topChannel ? ` يتصدر ${topChannel.label === "Online Store" ? "المتجر الإلكتروني" : "المتجر الفعلي"} توزيع القنوات، مع ${fmt.formatNumber(lowStock)} منتج بمخزون منخفض.` : ` يوجد ${fmt.formatNumber(lowStock)} منتج بمخزون منخفض.`}`
        : `Revenue reached ${fmt.formatCompactCurrency(kpis.totalRevenue)} across ${fmt.formatNumber(kpis.totalOrders)} orders, having ${movement(revenueDelta, isRTL)} versus the prior period. Active customers stand at ${fmt.formatNumber(kpis.activeCustomers)} with an average CLTV of ${fmt.formatCurrency(kpis.avgCltv)}, and average order value of ${fmt.formatCurrency(kpis.avgOrderValue)}, which ${movement(aovDelta, isRTL)}.${topChannel ? ` ${topChannel.label} leads channel distribution, with ${fmt.formatNumber(lowStock)} products running low on stock.` : ` ${fmt.formatNumber(lowStock)} products are running low on stock.`}`;
    }
    return "";
  }

  if (tab === "sales") {
    const orders = params.orders ?? [];
    const completed = orders.filter(order => order.status === "completed");
    const totalRevenue = completed.reduce((sum, order) => sum + order.total, 0);
    const cancelled = orders.filter(order => order.status === "cancelled");
    const pending = orders.filter(order => order.status === "pending");
    const refunded = orders.filter(order => order.status === "refunded");
    const refundValue = refunded.reduce((sum, order) => sum + order.total, 0);
    const cancelRate = orders.length > 0 ? cancelled.length / orders.length : 0;
    const avgOrderValue = completed.length > 0 ? totalRevenue / completed.length : 0;
    const channelData = params.channelData ?? [];
    const topChannel = [...channelData].sort((a, b) => b.value - a.value)[0];

    return isRTL
      ? `حققت ${fmt.formatNumber(completed.length)} طلب مكتمل إيرادات إجمالية بلغت ${fmt.formatCompactCurrency(totalRevenue)}، بمعدل إلغاء ${Math.round(cancelRate * 100)}%. متوسط قيمة الطلب ${fmt.formatCurrency(avgOrderValue)}، مع ${fmt.formatNumber(pending.length)} طلب قيد الانتظار.${topChannel ? ` يتصدر ${topChannel.label === "Online Store" ? "المتجر الإلكتروني" : "المتجر الفعلي"} توزيع المبيعات، وبلغت المبالغ المستردة ${fmt.formatCompactCurrency(refundValue)}.` : ` بلغت المبالغ المستردة ${fmt.formatCompactCurrency(refundValue)}.`}`
      : `${fmt.formatNumber(completed.length)} completed orders generated ${fmt.formatCompactCurrency(totalRevenue)} in revenue, with a ${Math.round(cancelRate * 100)}% cancellation rate. Average order value stands at ${fmt.formatCurrency(avgOrderValue)}, with ${fmt.formatNumber(pending.length)} orders still pending.${topChannel ? ` ${topChannel.label} leads the sales split, and refunds totalled ${fmt.formatCompactCurrency(refundValue)}.` : ` Refunds totalled ${fmt.formatCompactCurrency(refundValue)}.`}`;
  }

  if (tab === "inventory") {
    const products = params.products ?? [];
    const inStock = products.filter(product => product.status === "in_stock").length;
    const lowStock = products.filter(product => product.status === "low_stock").length;
    const outOfStock = products.filter(product => product.status === "out_of_stock").length;
    const inventoryValue = products.reduce((sum, product) => sum + product.totalStock * product.cost, 0);
    const totalZidStock = products.reduce((sum, product) => sum + product.zidStock, 0);
    const totalOdooStock = products.reduce((sum, product) => sum + product.odooStock, 0);
    const strongerSource = totalZidStock >= totalOdooStock ? "Zid" : "Odoo";

    return isRTL
      ? `يضم الكتالوج الموحّد ${fmt.formatNumber(products.length)} منتج بقيمة مخزون ${fmt.formatCompactCurrency(inventoryValue)}، منها ${fmt.formatNumber(inStock)} متوفر حالياً. يحتاج ${fmt.formatNumber(lowStock)} منتج إلى إعادة طلب، بينما نفد مخزون ${fmt.formatNumber(outOfStock)} منتج بالكامل. يحمل مصدر ${strongerSource} الحصة الأكبر من المخزون بين المصدرين المتصلين.`
      : `The unified catalog holds ${fmt.formatNumber(products.length)} products worth ${fmt.formatCompactCurrency(inventoryValue)} in stock, of which ${fmt.formatNumber(inStock)} are currently available. ${fmt.formatNumber(lowStock)} products need reordering and ${fmt.formatNumber(outOfStock)} are fully out of stock. ${strongerSource} carries the larger share of stock across the two connected sources.`;
  }

  if (tab === "customers") {
    const customers = params.customers ?? [];
    const segmentation = params.segmentation ?? [];
    const newSeg = segmentation.find(item => item.label === "New")?.value ?? 0;
    const returningSeg = segmentation.find(item => item.label === "Returning")?.value ?? 0;
    const totalSegmented = newSeg + returningSeg;
    const returningShare = totalSegmented > 0 ? Math.round((returningSeg / totalSegmented) * 100) : 0;
    const kpis = params.kpis;
    const previousKpis = params.previousKpis;
    const cltvDelta =
      kpis && previousKpis ? computeChangePercent(kpis.avgCltv, previousKpis.avgCltv) : null;
    const topSpender = [...customers].sort((a, b) => b.totalSpent - a.totalSpent)[0];

    return isRTL
      ? `تضم قاعدة العملاء ${fmt.formatNumber(customers.length)} عميل، منهم ${fmt.formatNumber(returningSeg)} عميل متكرر و${fmt.formatNumber(newSeg)} عميل جديد. يمثل العملاء المتكررون ${returningShare}% من القاعدة${kpis ? `، بمتوسط قيمة دائمة ${fmt.formatCurrency(kpis.avgCltv)} وقد ${movement(cltvDelta, isRTL)} مقارنة بالفترة السابقة` : ""}.${topSpender ? ` ${topSpender.nameAr} هو الأعلى إنفاقاً بإجمالي ${fmt.formatCurrency(topSpender.totalSpent)}.` : ""}`
      : `The customer base includes ${fmt.formatNumber(customers.length)} customers — ${fmt.formatNumber(returningSeg)} returning and ${fmt.formatNumber(newSeg)} new. Returning customers make up ${returningShare}% of the base${kpis ? `, at an average CLTV of ${fmt.formatCurrency(kpis.avgCltv)}, which ${movement(cltvDelta, isRTL)} versus the prior period` : ""}.${topSpender ? ` ${topSpender.name} is the top spender at ${fmt.formatCurrency(topSpender.totalSpent)}.` : ""}`;
  }

  const summary = params.summary;
  if (summary) {
    const margin = summary.revenue > 0 ? Math.round((summary.netProfit / summary.revenue) * 100) : 0;
    const previousSummary = params.previousSummary;
    const profitDelta = previousSummary
      ? computeChangePercent(summary.netProfit, previousSummary.netProfit)
      : null;
    const monthly = params.monthly ?? [];
    const bestMonth = [...monthly].sort((a, b) => b.profit - a.profit)[0];

    return isRTL
      ? `بلغت الإيرادات ${fmt.formatCompactCurrency(summary.revenue)} بصافي ربح ${fmt.formatCompactCurrency(summary.netProfit)} وهامش ${margin}%${previousSummary ? `، وقد ${movement(profitDelta, isRTL)} مقارنة بالفترة السابقة` : ""}. تكلفة البضاعة المباعة ${fmt.formatCompactCurrency(summary.cogs)} والمصروفات التشغيلية ${fmt.formatCompactCurrency(summary.expenses)}. الضرائب المحصّلة ${fmt.formatCompactCurrency(summary.taxes)} مقابل ${fmt.formatCompactCurrency(summary.refunds)} مبالغ مستردة${bestMonth ? `، وكان ${bestMonth.month} الأفضل ربحية` : ""}.`
      : `Revenue reached ${fmt.formatCompactCurrency(summary.revenue)} with a net profit of ${fmt.formatCompactCurrency(summary.netProfit)}, a ${margin}% margin${previousSummary ? `, which ${movement(profitDelta, isRTL)} versus the prior period` : ""}. Cost of goods sold came to ${fmt.formatCompactCurrency(summary.cogs)}, with operating expenses of ${fmt.formatCompactCurrency(summary.expenses)}. Taxes collected totalled ${fmt.formatCompactCurrency(summary.taxes)} against ${fmt.formatCompactCurrency(summary.refunds)} in refunds${bestMonth ? `, and ${bestMonth.month} was the strongest month for profitability` : ""}.`;
  }
  return "";
}

function buildAction(tab: OmnichannelSummaryTab, params: BuildSummaryParams): string | null {
  const { fmt } = params;
  const { isRTL } = fmt;

  if (tab === "overview") {
    const products = params.products ?? [];
    const lowStock = products.filter(product => product.status === "low_stock").length;
    if (lowStock > 0) {
      return isRTL
        ? `أعد تجديد المخزون لـ ${lowStock} منتج قبل أن ينفد بالكامل.`
        : `Reorder stock for the ${lowStock} low-stock products before they run out.`;
    }
    return isRTL
      ? "استمر في تعزيز القناة الأعلى أداءً لزيادة الإيرادات."
      : "Keep doubling down on the best-performing channel to grow revenue.";
  }

  if (tab === "sales") {
    const orders = params.orders ?? [];
    const cancelled = orders.filter(order => order.status === "cancelled").length;
    const cancelRate = orders.length > 0 ? cancelled / orders.length : 0;
    if (cancelRate > 0.12) {
      return isRTL
        ? "راجع أسباب الإلغاء المتكررة لتقليل فقدان الإيرادات."
        : "Investigate recurring cancellation causes to curb revenue leakage.";
    }
    return isRTL
      ? "وجّه الحملات التسويقية نحو القناة الأعلى تحويلاً."
      : "Direct marketing spend toward the channel converting best.";
  }

  if (tab === "inventory") {
    const products = params.products ?? [];
    const lowStock = products.filter(product => product.status === "low_stock");
    const outOfStock = products.filter(product => product.status === "out_of_stock");
    if (outOfStock.length > 0) {
      return isRTL
        ? `أعد تعبئة ${outOfStock.length} منتج نفد مخزونه بشكل عاجل.`
        : `Urgently restock the ${outOfStock.length} products that are out of stock.`;
    }
    if (lowStock.length > 0) {
      return isRTL
        ? `جدول إعادة طلب للمنتجات ذات المخزون المنخفض قبل نفادها.`
        : "Schedule reorders for low-stock products before they run out.";
    }
    return isRTL
      ? "وازن مستويات المخزون بين Zid وOdoo لتفادي فجوات العرض."
      : "Rebalance stock levels between Zid and Odoo to avoid supply gaps.";
  }

  if (tab === "customers") {
    return isRTL
      ? "أطلق حملة ولاء تستهدف العملاء المتكررين لرفع القيمة الدائمة."
      : "Launch a loyalty campaign targeting returning customers to lift lifetime value.";
  }

  if (tab === "financials") {
    const summary = params.summary;
    if (summary && summary.revenue > 0 && summary.refunds / summary.revenue > 0.05) {
      return isRTL
        ? "حقق في أسباب ارتفاع المبالغ المستردة لحماية هامش الربح."
        : "Investigate elevated refund levels to protect margin.";
    }
    return isRTL
      ? "راقب المصروفات التشغيلية للحفاظ على نمو صافي الربح."
      : "Monitor operating expenses to sustain net profit growth.";
  }

  return null;
}

function formatPeriodLabel(startIso: string, endIso: string, isRTL: boolean): string {
  const start = new Date(`${startIso}T00:00:00Z`);
  const end = new Date(`${endIso}T00:00:00Z`);
  const formatter = new Intl.DateTimeFormat(isRTL ? "ar-EG-u-nu-arab" : "en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
  return `${formatter.format(start)} – ${formatter.format(end)}`;
}

export interface OmnichannelExecutiveSummary extends DashboardNarrativeContent {
  hasData: boolean;
  periodLabel: string;
  action: string | null;
}

export function generateOmnichannelExecutiveSummary(
  params: BuildSummaryParams,
): OmnichannelExecutiveSummary {
  const { tab, fmt, dateRange } = params;
  const periodLabel = formatPeriodLabel(dateRange.startDate, dateRange.endDate, fmt.isRTL);

  const hasData =
    (tab === "overview" && Boolean(params.kpis) && (params.kpis?.totalOrders ?? 0) > 0) ||
    (tab === "sales" && (params.orders?.length ?? 0) > 0) ||
    (tab === "inventory" && (params.products?.length ?? 0) > 0) ||
    (tab === "customers" && (params.customers?.length ?? 0) > 0) ||
    (tab === "financials" && Boolean(params.summary) && (params.summary?.revenue ?? 0) > 0);

  if (!hasData) {
    return {
      summary: "",
      keyInsights: [],
      recommendations: [],
      hasData: false,
      periodLabel,
      action: null,
    };
  }

  let insights: string[];
  if (tab === "overview") {insights = buildOverviewInsights(params);}
  else if (tab === "sales") {insights = buildSalesInsights(params);}
  else if (tab === "inventory") {insights = buildInventoryInsights(params);}
  else if (tab === "customers") {insights = buildCustomersInsights(params);}
  else {insights = buildFinancialsInsights(params);}

  const lowDataThreshold = tab === "sales" ? (params.orders?.length ?? 0) < 30
    : tab === "customers" ? (params.customers?.length ?? 0) < 10
    : tab === "inventory" ? (params.products?.length ?? 0) < 5
    : false;
  const summarySegments: DashboardNarrativeSegment[] = lowDataThreshold
    ? [{ text: fmt.isRTL ? "بيانات محدودة لهذه الفترة — " : "Limited data for this period — ", type: "text" }]
    : [];

  const action = buildAction(tab, params);
  const leadSummary = buildLeadSummary(tab, params) || (insights[0] ?? "");

  return {
    summary: leadSummary,
    summarySegments: summarySegments.length ? [...summarySegments, { text: leadSummary, type: "text" }] : undefined,
    keyInsights: insights,
    recommendations: action ? [action] : [],
    hasData: true,
    periodLabel,
    action,
  };
}
